import { useCallback, useEffect, useRef, useState } from 'react';
import { getJob, submitJob } from './api';

const POLL_MS = 1000;
// Belt and braces: the server already reports an interrupted job as an
// error, but a wedged connection should not spin forever either.
const CLIENT_TIMEOUT_MS = 4 * 60 * 1000;

/**
 * Run one analysis as a background job and report where it got to.
 *
 *   const { status, result, error, elapsed, run, reset } = useJob();
 *   run('fastest', { year: 2025, race: '…', session: 'Race' });
 *
 * status: idle | queued | running | done | error
 *
 * `elapsed` ticks locally so the progress readout moves even while the
 * server is silent — a spinner with no number is the thing that makes a
 * 40-second wait feel like a hang.
 */
export default function useJob() {
  const [status, setStatus] = useState('idle');
  const [id, setId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const timerRef = useRef(null);
  const startedRef = useRef(0);
  const cancelledRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  const reset = useCallback(() => {
    clear();
    cancelledRef.current = true;
    setStatus('idle');
    setId(null);
    setResult(null);
    setError(null);
    setElapsed(0);
  }, [clear]);

  useEffect(() => () => { cancelledRef.current = true; clear(); }, [clear]);

  const run = useCallback(async (funcName, inputList) => {
    clear();
    cancelledRef.current = false;
    setStatus('queued');
    setId(null);
    setResult(null);
    setError(null);
    setElapsed(0);
    startedRef.current = Date.now();

    const tick = setInterval(() => {
      if (!cancelledRef.current) setElapsed((Date.now() - startedRef.current) / 1000);
    }, 250);

    const stopTicking = () => { clearInterval(tick); };

    const poll = async (id) => {
      for (;;) {
        if (cancelledRef.current) return;
        if (Date.now() - startedRef.current > CLIENT_TIMEOUT_MS) {
          setStatus('error');
          setError('This is taking longer than expected. Try running it again.');
          return;
        }
        await new Promise((r) => { timerRef.current = setTimeout(r, POLL_MS); });
        if (cancelledRef.current) return;
        let state;
        try {
          state = await getJob(id);
        } catch (e) {
          // One dropped poll should not kill a job that is still running.
          continue;
        }
        if (state.status === 'done') {
          setResult(state.result);
          setStatus('done');
          return;
        }
        if (state.status === 'error') {
          setError(state.error || 'The analysis failed.');
          setStatus('error');
          return;
        }
        setStatus(state.status === 'queued' ? 'queued' : 'running');
      }
    };

    try {
      const first = await submitJob(funcName, inputList);
      if (cancelledRef.current) { stopTicking(); return; }
      setId(first.id);
      if (first.status === 'done') {
        setResult(first.result);
        setStatus('done');
        stopTicking();
        return;
      }
      if (first.status === 'error') {
        setError(first.error || 'The analysis failed.');
        setStatus('error');
        stopTicking();
        return;
      }
      setStatus('running');
      await poll(first.id);
    } catch (e) {
      if (!cancelledRef.current) {
        setStatus('error');
        setError(e.message || 'Could not reach the analysis service.');
      }
    } finally {
      stopTicking();
      setElapsed((Date.now() - startedRef.current) / 1000);
    }
  }, [clear]);

  return { status, id, result, error, elapsed, run, reset };
}
